import type {
  TaskAssigneeFigures,
  TasksReportPayload,
} from '../../../hooks/pipelineTracker/useTasksReport';

/**
 * The Operations > Tasks table's row groups (V6): brokers first, then back-office owners, each
 * with a subtotal.
 *
 * The subtotals come from the SERVER, folded in the same pass as the All row, so the three can
 * never disagree through a rounding or a missed person on this side. This function only decides
 * which person sits under which heading, and it puts every person under exactly one: a person the
 * server did not tag (an older payload) falls to back office rather than out of the table.
 *
 * Returns null when the owners were not split, and the table then renders one list as it always
 * did, with the reason stated beside it.
 */
export interface TaskOwnerGroup {
  key: 'broker' | 'back_office';
  label: string;
  people: TaskAssigneeFigures[];
  subtotal: TaskAssigneeFigures;
}

export function groupTaskOwners(report: Pick<TasksReportPayload, 'assignees' | 'ownerSubtotals'>): TaskOwnerGroup[] | null {
  if (!report.ownerSubtotals) return null;
  const brokers = report.assignees.filter((a) => a.ownerRole === 'broker');
  const backOffice = report.assignees.filter((a) => a.ownerRole !== 'broker');
  return [
    { key: 'broker', label: 'Brokers', people: brokers, subtotal: report.ownerSubtotals.broker },
    {
      key: 'back_office',
      label: 'Back office',
      people: backOffice,
      subtotal: report.ownerSubtotals.backOffice,
    },
  ];
}

/** Overdue as a share of outstanding, whole percent; null when nothing is outstanding. */
export function overduePct(figures: Pick<TaskAssigneeFigures, 'totalOverdue' | 'outstanding'>): number | null {
  if (!Number.isFinite(figures.outstanding) || figures.outstanding <= 0) return null;
  return Math.round((figures.totalOverdue / figures.outstanding) * 100);
}
